import { CascadiaColor } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import type { AriaSelectProps } from "@react-types/select";
import React from "react";
import { HiddenSelect, useButton, useSelect } from "react-aria";
import { ComboBoxState, useSelectState } from "react-stately";

import { ListBox } from "./ListBox";
import { Popover } from "./Popover";
import { FieldErrorsProps } from "../../components/inputs/_assets/input-wrapper/FieldErrors";
import { FormFeedback } from "../../components/inputs/_assets/input-wrapper/FormFeedback";
import { FormLabel } from "../../components/inputs/_assets/input-wrapper/FormLabel";
import { FormText } from "../../components/inputs/_assets/input-wrapper/FormText";
import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";
import { useColors } from "../../hooks/useColors";

export interface SelectProps<T> extends Partial<AriaSelectProps<T>> {
  label?: string;
  children: React.ReactElement[] | React.ReactElement;
  minPopoverWidth?: number;
  maxListboxHeight?: number;
  isReadOnly?: boolean;
  helper?: string;
  hasError?: boolean;
  testID?: string;
  displayAs?: React.ReactElement;
  backgroundColor?: CascadiaColor;
  width?: string;
  errors?: FieldErrorsProps["children"];
  info?: React.ReactNode;
}

/**
 * @deprecated use SelectRAC instead - based on React Aria Components
 */
export function Select<T extends object>(props: SelectProps<T>) {
  const state = useSelectState(props);
  const ref = React.useRef<HTMLButtonElement>(null);
  const {
    isReadOnly,
    label,
    errors,
    name,
    isRequired,
    hasError,
    helper,
    placeholder,
    testID = "select",
    maxListboxHeight,
    displayAs,
    backgroundColor,
    width,
  } = props;
  const { labelProps, triggerProps, menuProps } = useSelect(props, state, ref);
  const minPopoverWidth = ref?.current?.clientWidth;
  const { buttonProps } = useButton(triggerProps, ref);
  const isDisabled = props.isDisabled || isReadOnly;
  const error = Boolean(errors || hasError);
  const colors = useColors();

  const handleMouseUp = () => {
    state.toggle();
  };

  const getBackgroundColor = () => {
    if (isDisabled) {
      return "var(--background-accent-neutral-weak)";
    }
    return colors[backgroundColor ?? "background-input"];
  };

  const borderColor = (hover = false) => {
    if (hover && error) return "var(--border-danger-input)";
    if (isDisabled) return "var(--border-input-disabled)";
    if (error) return "var(--border-danger-input)";
    if (hover) return "var(--border-input-active)";

    return "var(--border-strong)";
  };

  return (
    <>
      <FormLabel {...labelProps} isRequired={isRequired} info={props.info}>
        {label}
      </FormLabel>
      <HiddenSelect state={state} triggerRef={ref} label={label} name={name} />
      <button
        className={css(styles.selectButton, {
          border: `1px solid ${borderColor()}`,
          width: width ?? "100%",
          minWidth: "80px",
          background: getBackgroundColor(),
          "&:hover": css({
            border: `1px solid ${borderColor(true)}`,
          }),
          "&:focus": error
            ? css([
                {
                  outline: "0",
                  borderColor: "var(--border-danger)",
                  background: "var(--background-input)",
                  boxShadow: "var(--focus-input-error)",
                },
              ])
            : css([
                {
                  outline: "0",
                  borderColor: "var(--border-input-active)",
                  boxShadow: "var(--focus-input)",
                },
              ]),
        })}
        {...buttonProps}
        // accessible menu open on mouse up with pointer cancellation
        // https://www.w3.org/WAI/WCAG21/Understanding/pointer-cancellation.html
        onMouseDown={undefined}
        onPointerDown={undefined}
        onMouseUp={handleMouseUp}
        ref={ref}
        data-testid={testID}
      >
        {state.selectedItem ? (
          displayAs || (
            <div
              className={css({
                textAlign: "initial",
                whiteSpace: "nowrap",
                overflow: "hidden",
                flexWrap: "nowrap",
              })}
            >
              <Text
                color={isDisabled ? "text-disabled" : "text-base"}
                style="input/text-field"
              >
                {state.selectedItem.rendered}
              </Text>
            </div>
          )
        ) : (
          <Text style="input/text-field" color="text-placeholder">
            {placeholder}
          </Text>
        )}
        <Icon
          color={
            isDisabled || isReadOnly ? "border-input-disabled" : "icon-base"
          }
          size="space-5"
          type="standard"
          name={state.isOpen ? "expand_less" : "expand_more"}
        />
      </button>
      {helper && <FormText>{helper}</FormText>}
      {errors && <FormFeedback type="invalid">{errors}</FormFeedback>}
      {state.isOpen && (
        <Popover
          state={state}
          triggerRef={ref}
          placement="bottom start"
          minPopoverWidth={(minPopoverWidth as number) + 2}
        >
          <ListBox
            {...menuProps}
            state={state as ComboBoxState<unknown>}
            maxHeight={maxListboxHeight}
          />
        </Popover>
      )}
    </>
  );
}

const styles = {
  selectButton: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: "var(--border-radius-2)",
    padding: `${"var(--space-2)"} ${"var(--space-3)"}`,
    minWidth: "80px",
  }),
};
